import type { ThemeUIStyleObject } from '@theme-ui/core';
import { Waypoint } from 'react-waypoint';

import { Separator } from '@change/ds-core-ui/next';

import { Translate } from '@change-corgi/core/react/i18n';
import { Link } from '@change-corgi/design-system-legacy/components/actions';
import { Card } from '@change-corgi/design-system-legacy/components/content';
import { Icon } from '@change-corgi/design-system-legacy/components/icon';
import { Loader } from '@change-corgi/design-system-legacy/components/progressiveDisclosure';
import { iconInfoOutline } from '@change-corgi/design-system-legacy/icons';
import { Box, Flex } from '@change-corgi/design-system-legacy/layout';
import { Heading, Text } from '@change-corgi/design-system-legacy/typography';

import type { WaypointCallbackArgs } from 'src/app/shared/components/layout/lazy';

import { RecommendationsCarousel } from './components/RecommendationsCarousel';
import { ShareUrl } from './components/ShareUrl';
import { useRecommendations } from './hooks/useRecommendations';
import { useShareRecommendations } from './hooks/useShareRecommendations';

const styles: Record<string, ThemeUIStyleObject> = {
	betaTag: {
		alignItems: 'center',
		display: 'flex',
		justifyContent: 'center',
		padding: '2px 8px',
	},
	mainCard: {
		border: 'unset',
		borderRadius: 'big',
		display: 'flex',
		flexDirection: 'column',
		gap: '24px',
		paddingY: '24px',
		width: '100%',
	},
	separator: {
		borderColor: 'neutral-grey100',
		borderStyle: 'solid none none none',
		display: ['inline', 'none'],
		marginTop: 8,
		marginBottom: 8,
	},
};

type Props = {
	slug: string;
};

/*
  Please note that this component has a combination of legacy and next (sunrise) components.
  This is temporary and only for the sake of experimentation. In case that this variant is chosen
  as winner, we will need to plan the migration of all components to sunrise.
 */
export function RecommendationsVariant2({ slug }: Props): JSX.Element | null {
	const {
		data: { shareRecommendationsLoading, shouldDisplayShareRecommendations, shareRecommendations },
	} = useShareRecommendations(slug);

	const { actions } = useRecommendations();

	if (shareRecommendationsLoading) {
		return <Loader size="m" my={64} mx="auto" data-testid="share-recommendations-loading" />;
	}

	if (!shouldDisplayShareRecommendations) {
		return null;
	}

	const firstRedditRec = shareRecommendations.reddit.at(0);
	const firstFacebookRec = shareRecommendations.facebook.at(0);

	return (
		<Box as="section" aria-labelledby="recommendations-title">
			<Waypoint
				onPositionChange={(waypointCallbackArgs: WaypointCallbackArgs) => {
					actions.trackView(waypointCallbackArgs, firstFacebookRec?.metadata.source, firstRedditRec?.metadata.source);
				}}
			/>
			<Card backgroundColor="white" sx={styles.mainCard}>
				<Flex sx={{ flex: '1 0 0', flexDirection: 'column', gap: '16px', paddingX: '24px' }}>
					<Heading as="h2" size="h4" id="recommendations-title" data-qa="recommendations-title">
						Share in recommended groups
					</Heading>
					<Text size="default">
						{actions.translateHtml('corgi.petition.starter_dashboard.recommendations.description_variant')}&nbsp;
						<Link
							variant="primary"
							to="#"
							onClick={async (e) => {
								e.preventDefault();
								await actions.handleTipClick();
							}}
							sx={{ display: 'inline-flex', color: 'primary-black', fontWeight: '400', alignItems: 'center' }}
							data-qa="recommendations-tip-button"
						>
							<Text size="default">
								<Flex sx={{ alignItems: 'center', gap: 3 }}>
									<Translate value="corgi.petition.starter_dashboard.recommendations.sharing_tips" />
									<Icon icon={iconInfoOutline} color="currentColor" size="m" />
								</Flex>
							</Text>
						</Link>
					</Text>
					<div className="max-w-full md:max-w-[50%]">
						<ShareUrl />
					</div>
				</Flex>
				<div className="mx-6 my-0">
					<Separator className="m-0" />
				</div>
				<RecommendationsCarousel shareRecommendations={shareRecommendations} />
			</Card>
		</Box>
	);
}
